<template>
  <div class="wrapper">
    <div class="tw-container tw-mx-auto tw-transition-all lg:tw-px-32 md:tw-px-16">
      
      <StepProgress
        v-if="!state.editMode"
        :step="state.step"
      />

      <div class="tw-bg-white tw-shadow-xl tw-rounded-3xl tw-overflow-hidden tw-min-h-32 tw-mb-12">
        <LocationsView 
          v-if="state.step == 1" 
          :selectedLocation="state.selectedLocation"
          @order-type-set="setOrderType"
          @select-location="setLocation" />

        <EventInfoView 
          :guestCount="state.cateringOrder.guest_count"
          @guest-count-set="setGuestCount"
          :eventType="state.cateringOrder.event_type"
          @event-type-set="setEventType"
          v-if="state.step == 2" />

        <GuestInfoView 
          :catering-order="state.cateringOrder"
          v-if="state.step == 3" />

        <TimeWanted 
          :handoffMode="state.handoffMode"
          :selected-location="state.selectedLocation"
          :guestCount="state.cateringOrder.guest_count"
          :editMode="state.editMode"
          @time-wanted-set="setTimeWanted"
          :time-wanted="state.timeWanted"
          v-if="state.step == 4 && state.selectedLocation" />
      </div>

      <div class="tw-flex tw-flex-wrap tw-flex-row tw-mx-8 lg:tw-mx-auto">
        <div class="tw-w-full lg:tw-w-auto lg:tw-ml-auto lg:tw-order-3">
          <TwButton 
            cssClass="tw-mr-4 tw-w-full lg:tw-w-auto tw-bg-transparent tw-border-gray-400 tw-border tw-rounded-full tw-text-gray-500 hover:tw-border-orange-medium hover:tw-text-orange-medium tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4 tw-transition-all tw-duration-200" 
            @emit-click="nextStep()" 
            v-if="stepIsSkippable && !state.editMode"
            :isDisabled="false" 
            buttonText="Skip"/>
          <TwButton 
            cssClass="tw-block tw-w-full lg:tw-w-auto md:tw-inline-block tw-bg-orange-medium hover:tw-bg-orange-dark tw-border-orange-medium tw-border tw-rounded-full tw-text-white tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4  tw-transition-all tw-duration-200" 
            @emit-click="nextStep()" 
            :isLoading="state.submittingCateringOrder"
            :isDisabled="false" 
            :buttonText="!state.editMode ? 'Next' : 'Update'"/>
        </div>

        <TwButton 
          cssClass="lg:tw-order-1 tw-w-1/2 lg:tw-w-auto lg:tw-flex-grow-0 lg:tw-mr-4 tw-bg-transparent tw-border-gray-400 tw-border tw-rounded-full tw-text-gray-500 hover:tw-border-orange-medium hover:tw-text-orange-medium tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4 tw-transition-all tw-duration-200"
          @emit-click="prevStep()" 
          v-if="state.step > 1 && (!state.editMode || state.editMode && state.step == 3 )" 
          :isDisabled="false" 
          buttonText="Back"/>

        <TwButton 
          cssClass="lg:tw-order-2 tw-w-1/2 lg:tw-flex-grow-0 lg:tw-w-auto lg:tw-mr-4 tw-bg-transparent tw-border-gray-400 tw-border tw-rounded-full tw-text-gray-500 hover:tw-border-orange-medium hover:tw-text-orange-medium tw-uppercase tw-font-bold tw-px-16 tw-mb-4 tw-py-4 tw-transition-all tw-duration-200" 
          @emit-click="cancelEdit" 
          v-if="state.editMode"
          :isDisabled="false" 
          buttonText="Cancel"/>

      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import {
    defineComponent, reactive, computed, onMounted
  } from 'vue';
  import { useRoute, useRouter } from 'vue-router'
  // import { storeToRefs } from 'pinia'
  import TwButton from '../../components/TwButton.vue'

  // Data
  import type GuestInfo from '../../types/GuestInfo'
  import type CateringOrder from '../../types/CateringOrder'
  import type FlashMessage from '../../types/FlashMessage'
  import type Location from '../../classes/Location';

  import { useBasketStore } from "../../stores/basket";
  import { useUserStore } from "../../stores/user";
  import { useCateringOrderStore } from "../../stores/catering_order";
  import { useLocationsStore } from "../../stores/locations";
  import { useFlashMessageStore } from '../../stores/flashMessages';

  // components
  import StepProgress from './_StepProgress.vue'
  import LocationsView from './LocationsView.vue'
  import EventInfoView from './EventInfoView.vue'
  import GuestInfoView from './GuestInfoView.vue'
  import TimeWanted from './TimeWantedView.vue'
  import { DateTime } from 'luxon';

  const route = useRoute();
  const router = useRouter();

  const basketStore = useBasketStore();
  const userStore = useUserStore();
  const cateringOrderStore = useCateringOrderStore();
  const locationsStore = useLocationsStore();
  const flashMessageStore = useFlashMessageStore();
  // const store = useUserStore();
  const state = reactive({
    step: 1,
    eventType: '',
    selectedLocation: undefined as (Location | undefined),
    handoffMode: 'pickup',
    timeWanted: undefined as (Date | undefined),
    timeWantedDirty: false,
    editMode: false,
    submittingCateringOrder: false,
    cateringOrder: {
      id: 0,
      guest_count: 0,
      event_type: "",
      light_eaters: 0,
      regular_eaters: 0,
      hungry_eaters: 0,
      store: 0,
      time_wanted: '',
      delivery_mode: '',
      basket_id: "",
      dietary_restrictions: {
        glutenAllergen: false,
        dairyAllergen: false,
        nutAllergen: false,
        vegan: false,
        vegetarian: false,
        heartHealthy: false,
      },
    } as CateringOrder,
  })

  onMounted(() => {
    if (cateringOrderStore.cateringOrder) {
      const proxyOrder = JSON.parse(JSON.stringify(cateringOrderStore.cateringOrder));

      if (proxyOrder.time_wanted && proxyOrder.time_wanted != '') {
        let dt = DateTime.fromISO(cateringOrderStore.cateringOrder!.time_wanted);
        state.timeWanted = dt.toJSDate();
      }

      if (proxyOrder.delivery_mode) {
        state.handoffMode = proxyOrder.delivery_mode;
      }

      if (proxyOrder.dietary_restrictions) {
        // Parse the jsonb
      } else {
        proxyOrder.dietary_restrictions = {
          glutenAllergen: false,
          dairyAllergen: false,
          nutAllergen: false,
          vegan: false,
          vegetarian: false,
          heartHealthy: false,
        }
      }

      state.cateringOrder = proxyOrder;

      // Find the matching location
      if (locationsStore.locations) {
        const matchedLocation = locationsStore.locations.find((loc: Location) => {
          return loc.id == cateringOrderStore.cateringOrder!.store
        })

        state.selectedLocation = matchedLocation
      }
    }

    if (route.params.step) {
      state.editMode = true;
      state.step = parseFloat(route.params.step as string);
    } else {
      // Check if the user has a basket or selected location to start from
      if (basketStore.basket) {
        if (locationsStore.locations) {
          const matchedLocation = locationsStore.locations.find((loc: Location) => {
            return loc.id == basketStore.basket!.vendorid
          })

          state.selectedLocation = matchedLocation
        }
      }
    }
  })

  const cancelEdit = (() => {
    if (route.params && route.params.redirect && router.options.history.state.back) {
      router.push(router.options.history.state.back.toString())
    } else {
      router.push('/menu')
    }
  })

  function prevStep() {
    if (state.step > 1) {
      window.scrollTo(0,0);
      state.step -= 1;
      flashMessageStore.dismissErrors();
    }
  }
  function nextStep() {
    const stepValid = validateStep();
    if (stepValid) {
      flashMessageStore.dismissErrors();
      if (state.editMode) {
        if (parseFloat(route.params.step as string) == 2 && state.step == 2) {
          window.scrollTo(0,0);
          state.step += 1;
        } else {
          // User navigated to the wizard to edit only a single step
          submitCateringOrder().then(() => {
            if (route.params && route.params.redirect && router.options.history.state.back) {
              router.push(router.options.history.state.back.toString())
            } else {
              router.push('/menu')
            }
          })
        }
      } else {
        if (state.step < 4 && validateStep()) {
          window.scrollTo(0,0);
          state.step += 1;
        } else {
          submitCateringOrder().then(() => {
            router.push('/menu')
          })
        }
      }
    }
  }

  const submitCateringOrder = ((): Promise<any> => {
    state.submittingCateringOrder = true;
    return new Promise((resolve, reject) => {
      let order = {
        guest_count: state.cateringOrder.guest_count,
        event_type: state.cateringOrder.event_type,
        light_eaters: state.cateringOrder.light_eaters,
        regular_eaters: state.cateringOrder.regular_eaters,
        hungry_eaters: state.cateringOrder.hungry_eaters,
        store: state.selectedLocation!.id!,
        time_wanted: state.timeWanted!,
        delivery_mode: state.handoffMode,
        basket_id: basketStore.basket?.id,
        dietary_restrictions: state.cateringOrder.dietary_restrictions,
        assistance_level: cateringOrderStore.assistanceLevel,
      } as unknown as CateringOrder;

      if (basketStore.basket == null || basketStore.basket != null && basketStore.basket!.vendorid != state.selectedLocation!.id) {
        order.basket_id = "";
      }

      userStore.setUserLocation(state.selectedLocation!);

      // If the user has a basket with a different time wanted or no timewanted set; we will have to update it if the timewanted changed
      if (timeWantedNeedsUpdated()) {
        let tw = DateTime.fromJSDate(state.timeWanted!);
        basketStore.setTimeWanted(tw, false).then(() => {
          if (state.cateringOrder.id) {
            cateringOrderStore.updateCateringOrder(state.cateringOrder.id, order).then(() => {
              state.submittingCateringOrder = false;
              resolve('');
            }).catch((error: Error) => {
              state.submittingCateringOrder = false;
              errorFlash('Error', error.message)
            })
          } else {
            cateringOrderStore.createCateringOrder(order).then(() => {
              state.submittingCateringOrder = false;
              resolve('');
            }).catch((error: Error) => {
              state.submittingCateringOrder = false;
              errorFlash('Error', error.message)
            })
          }
        }).catch((error: Error) => {
          state.submittingCateringOrder = false;
           errorFlash('Error', error.message)
        })
      } else {
        if (state.cateringOrder.id) {
          cateringOrderStore.updateCateringOrder(state.cateringOrder.id, order).then(() => {
            state.submittingCateringOrder = false;
            resolve('');
          }).catch((error: Error) => {
            state.submittingCateringOrder = false;
            errorFlash('Error', error.message)
          })
        } else {
          cateringOrderStore.createCateringOrder(order).then(() => {
            state.submittingCateringOrder = false;
            resolve('');
          }).catch((error: Error) => {
            state.submittingCateringOrder = false;
            errorFlash('Error', error.message)
          })
        }
      }
    })
  })

  const timeWantedNeedsUpdated = (():boolean => {
    let bool = false;
    if (basketStore.basket) {
      if (basketStore.basket.timewanted && state.timeWantedDirty == true) {
        const requestedTW = DateTime.fromJSDate(state.timeWanted!);
        const basketTW =  DateTime.fromFormat(basketStore.basket.timewanted, 'yyyyMMdd hh:mm');

        bool = (requestedTW != basketTW);
      }
    }

    return bool;
  })

  const stepIsSkippable = computed(() => {
    return false //(state.step == 2 || state.step == 3)
  })

  function validateStep(): boolean {
    let valid = false;
    if (state.step == 1) {
      const locationValid = (state.selectedLocation != null && typeof(state.selectedLocation.id) != undefined);
      const handoffModeValid = (state.handoffMode != null);
      valid = locationValid && handoffModeValid;

      if (!valid) {
        errorFlash('Error', 'Please select a location for your order.')
      }
    } else if (state.step == 2) {
      const guestCountValid = state.cateringOrder.guest_count != null && state.cateringOrder.guest_count > 0;
      const eventTypeValid = state.cateringOrder.event_type != null && state.cateringOrder.event_type != '';
      valid = guestCountValid && eventTypeValid;

      if (!valid) {
        errorFlash('Error', 'Please set the guest count and event type for your order.')
      }
    } else if (state.step == 3) {
      let totalFeeds = (state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters);
      if (totalFeeds == state.cateringOrder.guest_count) {
        valid = true;
      } else {
        // let diff = state.cateringOrder.guest_count - (state.cateringOrder.light_eaters + state.cateringOrder.regular_eaters + state.cateringOrder.hungry_eaters);
        // state.cateringOrder.regular_eaters = Math.max((state.cateringOrder.regular_eaters + diff),0);
        valid = false;
        if (totalFeeds < state.cateringOrder.guest_count) {
          errorFlash('Error', 'You have selected less guests to feed than your provided number of anticipated guests in the previous step. Please update the number of anticipated guests in the previous step or update your current counts above to match.')
        } else {
        errorFlash('Error', 'You have selected more guests to feed than your provided number of anticipated guests in the previous step. Please update the number of anticipated guests in the previous step or update your current counts above to match.')
        }
      } 

    }
    else if (state.step == 4) {
      const timeWantedValid = (state.timeWanted != null && state.timeWanted > new Date);
      valid = timeWantedValid;

      if (!valid) {
        errorFlash('Error', 'Please set a valid time for your order.')
      }
    }
    
    return valid;
  }

  const setGuestCount = (amount: number) => {
    state.cateringOrder.guest_count = amount
  }

  const setEventType = (eventType: string) => {
    state.cateringOrder.event_type = eventType
  }

  const setLocation = (location: Location) => {
    state.selectedLocation = location

    nextStep();
  }

  const setOrderType = (mode: string) => {
    state.handoffMode = mode
  }

  const setTimeWanted = (time: Date) => {
    state.timeWanted = time
    state.timeWantedDirty = true;
  }

  const errorFlash = (title: string, text: string) => {
    const message = {
      id: Math.round(Date.now() / 1000),
      messageType: "danger",
      text: text,
      headline: title,
      delay: 0, // ms
      icon: "",
      cssClass: "",
    } as FlashMessage;
    flashMessageStore.addMessage(message);
  }
</script>